import type { AttemptOperationId } from '@sportpark/shared/operation-attempt';
import { http } from './http-client';
import { DayOfWeek } from './generated';

import type {
  AssignableClubRoleDto,
  AuditEntityTypeDto,
  BroadcastNotificationResponse,
  ChangeClubStaffContactsBody,
  ChangeClubStaffRoleBody,
  ClubJoinCodeResponse,
  ClubStaffContactsChanged,
  ClubStaffMemberDto,
  ClientCategoryRulePayload,
  ClubProfilePayload,
  ClubScheduleDto,
  ClubSchedulePayload,
  CreateClubRolePayload,
  CreateCourtOptionPayload,
  CreateCourtPayload,
  FriendsPolicyPayload,
  GridConfigDto,
  GridConfigPayload,
  RatingPolicyPayload,
  RoleConfigDto,
  RolePayload,
  SlotPolicyPayload,
  TaxConfigPayload,
  UpdateCourtOptionPayload,
  UpdateCourtPayload,
  UpdateReminderPayload,
  FitnessPolicyDto,
  IssuedClubJoinCodeResponse,
  FitnessPolicyPayload,
  GetUserPermissionsResponse,
  HeldDespiteRevoke,
  ListAuditEventsResponse,
  OwnerReportSettingsDto,
  PermissionCatalogueDto,
  RegisterClubStaffBody,
  SetOnlineReceiptsBody,
  TrainerCardWithoutLogin,
} from './generated';

import type { ClientCategory } from './clients-api';
import type {
  IntegrationKind,
  NotificationCategory as NotificationCategoryCode,
} from './generated';
import type {
  FriendsPolicyDto as FriendsPolicyConfig,
  IntegrationDto as IntegrationConfig,
  PaymentProviderDto as PaymentProviderConfig,
  IntegrationSecretDto,
  RatingPolicyDto as RatingPolicyConfig,
} from './generated';
import type { ClientCategoryRuleDto, CourtDto } from './generated';
import type { ClubProfileDto, SettingsAuditDto } from './generated';
import type { CourtOptionDto, NotificationPolicyDto, ReminderScheduleDto, SlotPolicyDto, TaxConfigDto } from './generated';

export type Role =
  | 'super_admin'
  | 'club_owner'
  | 'admin'
  | 'manager'
  | 'cashier'
  | 'trainer'
  | 'client'
  | 'org_admin';

export interface RoleConfig extends RoleConfigDto {
  roleCode: Role | `cr_${string}`;
  label: string;
  baseRole: Role | null;
  holders: number;
  capabilities: string[];
  description: string;
  builtInCapabilities: string[];
  revokedCapabilities: string[];
  editable: boolean;
  stillHeld: HeldDespiteRevoke[];
}

export interface ScheduleGridConfig {
  slotStepMin: 15 | 30 | 60;
  dayStartHour: number;
  dayEndHour: number;
  density: 'compact' | 'comfortable' | 'spacious';
  showTrainerStripes: boolean;
  tintByCourt: boolean;
}

export type IntegrationSettingsPatch = Record<string, string | number | boolean | null>;

export interface NotificationEventTemplate {
  id: string;
  eventType: string;
  channel: string;
  locale: string;
  titleTemplate: string;
  bodyTemplate: string;
  htmlBodyTemplate: string | null;
  variables: string[];
  isCustomized: boolean;
}

export type ReminderCreatePayload = Pick<
  ReminderScheduleDto,
  'minutesBefore' | 'channel' | 'appliesTo'
>;

const isSlotStep = (n: number): n is ScheduleGridConfig['slotStepMin'] => n === 15 || n === 30 || n === 60;

const BUILT_IN_ROLES: Record<Role, true> = {
  super_admin: true,
  club_owner: true,
  admin: true,
  manager: true,
  cashier: true,
  trainer: true,
  client: true,
  org_admin: true,
};
const isBuiltInRole = (code: string): code is Role => Object.hasOwn(BUILT_IN_ROLES, code);
const isClubRoleCode = (code: string): code is `cr_${string}` => code.startsWith('cr_');

export const settingsApi = {
  getFitnessPolicy: (call?: { signal?: AbortSignal }) => http.get<FitnessPolicyDto>('/settings/fitness', { signal: call?.signal }),
  patchFitnessPolicy: (patch: FitnessPolicyPayload) =>
    http.patch<FitnessPolicyDto>('/settings/fitness', patch),

  getClubProfile: (call?: { signal?: AbortSignal }) => http.get<ClubProfileDto>('/settings/club/profile', { signal: call?.signal }),

  issueClubJoinCode: () => http.post<ClubJoinCodeResponse>('/settings/club/join-code'),

  getIssuedClubJoinCode: async (call?: { signal?: AbortSignal }): Promise<ClubJoinCodeResponse | null> =>
    (await http.get<IssuedClubJoinCodeResponse>('/settings/club/join-code/issued', { signal: call?.signal })).issued ?? null,
  patchClubProfile: (patch: ClubProfilePayload) =>
    http.patch<ClubProfileDto>('/settings/club/profile', patch),

  getClubSchedule: (call?: { signal?: AbortSignal }) => http.get<ClubScheduleDto>('/settings/club/schedule', { signal: call?.signal }).then(adaptScheduleFromBe),
  patchClubSchedule: (patch: ClubSchedulePatch) =>
    http
      .patch<ClubScheduleDto>('/settings/club/schedule', adaptSchedulePatchToBe(patch))
      .then(adaptScheduleFromBe),

  getGrid: async (call?: { signal?: AbortSignal }): Promise<ScheduleGridConfig> => {
    const dto = await http.get<GridConfigDto>('/settings/grid', { signal: call?.signal });
    if (!isSlotStep(dto.slotStepMin)) {
      throw new Error(`Сервер прислал шаг сетки, которого витрина не знает: ${dto.slotStepMin} мин`);
    }
    return { ...dto, slotStepMin: dto.slotStepMin };
  },
  patchGrid: (patch: GridConfigPayload) =>
    http.patch<ScheduleGridConfig>('/settings/grid', patch),

  getSlotPolicy: (call?: { signal?: AbortSignal }) => http.get<SlotPolicyDto>('/settings/slot-policy', { signal: call?.signal }),
  patchSlotPolicy: (patch: SlotPolicyPayload) =>
    http.patch<SlotPolicyDto>('/settings/slot-policy', patch),

  getRatingPolicy: (call?: { signal?: AbortSignal }) => http.get<RatingPolicyConfig>('/settings/rating-policy', { signal: call?.signal }),
  patchRatingPolicy: (patch: RatingPolicyPayload) =>
    http.patch<RatingPolicyConfig>('/settings/rating-policy', patch),

  getFriendsPolicy: (call?: { signal?: AbortSignal }) => http.get<FriendsPolicyConfig>('/settings/friends', { signal: call?.signal }),
  patchFriendsPolicy: (patch: FriendsPolicyPayload) =>
    http.patch<FriendsPolicyConfig>('/settings/friends', patch),

  listCourts: (call?: { signal?: AbortSignal }) => http.get<{ items: CourtDto[] }>('/settings/courts?includeArchived=true', { signal: call?.signal }),
  createCourt: (payload: CreateCourtPayload) => http.post<CourtDto>('/settings/courts', payload),
  patchCourt: (id: string, patch: UpdateCourtPayload) =>
    http.patch<CourtDto>(`/settings/courts/${id}`, patch),
  archiveCourt: (id: string) => http.delete<void>(`/settings/courts/${id}`),

  listCourtOptions: (call?: { signal?: AbortSignal }) =>
    http.get<{ items: CourtOptionDto[] }>('/settings/court-options?includeDisabled=true', { signal: call?.signal }),
  createCourtOption: (payload: CreateCourtOptionPayload) =>
    http.post<CourtOptionDto>('/settings/court-options', payload),
  patchCourtOption: (id: string, patch: UpdateCourtOptionPayload) =>
    http.patch<CourtOptionDto>(`/settings/court-options/${id}`, patch),
  deleteCourtOption: (id: string) => http.delete<void>(`/settings/court-options/${id}`),

  listClientCategoryRules: (call?: { signal?: AbortSignal }) =>
    http.get<{ items: ClientCategoryRuleDto[] }>('/settings/client-category-rules', { signal: call?.signal }),
  createClientCategoryRule: (payload: {
    targetCategory: ClientCategory;
    minVisits: number;
    minLtv: number;
    lookbackDays: number;
    enabled?: boolean;
  }) => http.post<ClientCategoryRuleDto>('/settings/client-category-rules', payload),
  patchClientCategoryRule: (id: string, patch: ClientCategoryRulePayload) =>
    http.patch<ClientCategoryRuleDto>(`/settings/client-category-rules/${id}`, patch),
  deleteClientCategoryRule: (id: string) =>
    http.delete<void>(`/settings/client-category-rules/${id}`),


  getTax: (call?: { signal?: AbortSignal }) => http.get<TaxConfigDto>('/settings/tax', { signal: call?.signal }),
  patchTax: (patch: TaxConfigPayload) => http.patch<TaxConfigDto>('/settings/tax', patch),

  getChatQuickReplies: (call?: { signal?: AbortSignal }) => http.get<string[]>('/settings/chat-quick-replies', { signal: call?.signal }),
  putChatQuickReplies: (items: string[]) =>
    http.put<string[]>('/settings/chat-quick-replies', { items }),

  listReminders: (includeDisabled?: boolean, call?: { signal?: AbortSignal }) =>
    http.get<ReminderScheduleDto[]>('/settings/reminders', { query: { includeDisabled }, signal: call?.signal }),
  createReminder: (payload: ReminderCreatePayload) =>
    http.post<ReminderScheduleDto>('/settings/reminders', payload),
  patchReminder: (id: string, patch: UpdateReminderPayload) =>
    http.patch<ReminderScheduleDto>(`/settings/reminders/${id}`, patch),
  deleteReminder: (id: string) => http.delete<void>(`/settings/reminders/${id}`),

  getNotificationPolicy: (call?: { signal?: AbortSignal }) => http.get<NotificationPolicyDto>('/admin/notifications/policy', { signal: call?.signal }),
  putNotificationPolicy: (patch: {
    clientDisableableCategories?: NotificationCategoryCode[];
    quietHoursEnabled?: boolean;
    quietHoursFrom?: string;
    quietHoursTo?: string;
  }) => http.put<void>('/admin/notifications/policy', patch),

  getOwnerReports: (call?: { signal?: AbortSignal }) => http.get<OwnerReportSettingsDto>('/admin/notifications/owner-reports', { signal: call?.signal }),
  putOwnerReports: (patch: {
    shiftReportByMail?: boolean;
    dailySummaryByMail?: boolean;
    recipientRoles?: string[];
  }) => http.put<void>('/admin/notifications/owner-reports', patch),

  listNotificationTemplates: (call?: { signal?: AbortSignal }) =>
    http.get<NotificationEventTemplate[]>('/admin/notifications/templates', { signal: call?.signal }),
  updateNotificationTemplate: (
    id: string,
    payload: {
      titleTemplate: string;
      bodyTemplate: string;
      htmlBodyTemplate: string | null;
    },
  ) => http.put<void>(`/admin/notifications/templates/${id}`, payload),

  sendBroadcast: (payload: {
    title: string;
    body: string;
    category: NotificationCategoryCode;
    clientOperationId: AttemptOperationId;
    actionUrl?: string;
  }) => http.post<BroadcastNotificationResponse>('/admin/notifications/broadcast', payload),

  listIntegrations: (call?: { signal?: AbortSignal }) => http.get<IntegrationConfig[]>('/settings/integrations', { signal: call?.signal }),
  revealIntegrationSecret: (kind: IntegrationConfig['kind'], key: string, call?: { signal?: AbortSignal }) =>
    http.get<IntegrationSecretDto>(
      `/settings/integrations/${kind}/secret/${encodeURIComponent(key)}`, { signal: call?.signal },
    ),
  patchIntegration: (
    kind: IntegrationConfig['kind'],
    patch: { label?: string | null; enabled?: boolean; settings?: IntegrationSettingsPatch },
  ) => {
    const body: { label?: string | null; enabled?: boolean; settingsJson?: string } = {};
    if (patch.label !== undefined) body.label = patch.label;
    if (patch.enabled !== undefined) body.enabled = patch.enabled;
    if (patch.settings !== undefined) body.settingsJson = JSON.stringify(patch.settings);
    return http.patch<IntegrationConfig>(`/settings/integrations/${kind}`, body);
  },
  listPaymentProviders: (call?: { signal?: AbortSignal }) =>
    http.get<PaymentProviderConfig[]>('/settings/payment-providers', { signal: call?.signal }),
  patchPaymentProvider: (
    provider: string,
    patch: {
      enabled?: boolean;
      credentials?: Record<string, string>;
      handlesCards?: boolean;
      handlesSbp?: boolean;
      cardFeePct?: number;
      sbpFeePct?: number;
    },
  ) => http.patch<PaymentProviderConfig>(`/settings/payment-providers/${provider}`, patch),
  createWebhookAddress: (provider: string) =>
    http.post<PaymentProviderConfig>(
      `/settings/payment-providers/${provider}/webhook-address`,
      {},
    ),
  setOnlineReceipts: (provider: string, body: SetOnlineReceiptsBody) =>
    http.post<PaymentProviderConfig>(`/settings/payment-providers/${provider}/online-receipts`, body),

  listRoles: async (call?: { signal?: AbortSignal }): Promise<{ items: RoleConfig[]; refused: Array<{ roleCode: string }> }> => {
    const raw = await http.get<RoleConfigDto[] | { items?: RoleConfigDto[] }>('/settings/roles', { signal: call?.signal });
    const arr = Array.isArray(raw) ? raw : (raw.items ?? []);
    const items: RoleConfig[] = [];
    const refused: Array<{ roleCode: string }> = [];
    for (const r of arr) {
      const base = r.baseRole ?? null;
      const roleCode = isBuiltInRole(r.roleCode) || isClubRoleCode(r.roleCode) ? r.roleCode : null;
      const baseRole = base === null ? null : isBuiltInRole(base) ? base : undefined;
      if (roleCode === null || baseRole === undefined) {
        refused.push({ roleCode: r.roleCode });
        continue;
      }
      items.push({
        ...r,
        roleCode,
        label: r.label ?? roleCode,
        baseRole,
        holders: r.holders ?? 0,
        capabilities: r.capabilities ?? [],
        description: r.description ?? '',
        builtInCapabilities: r.builtInCapabilities ?? [],
        revokedCapabilities: r.revokedCapabilities ?? [],
        editable: r.editable ?? true,
        stillHeld: r.stillHeld ?? [],
      });
    }
    return { items, refused };
  },
  getPermissionCatalogue: (call?: { signal?: AbortSignal }) => http.get<PermissionCatalogueDto>('/settings/permissions/catalogue', { signal: call?.signal }),
  patchRole: (code: RoleConfig['roleCode'], patch: RolePayload) =>
    http.patch<RoleConfig>(`/settings/roles/${code}`, patch),
  createClubRole: (body: CreateClubRolePayload) =>
    http.post<RoleConfig>('/settings/roles', body),
  deleteClubRole: (code: string) => http.delete<void>(`/settings/roles/${code}`),
  listAssignableClubRoles: (call?: { signal?: AbortSignal }) => http.get<AssignableClubRoleDto[]>('/club/staff/roles', { signal: call?.signal }),

  listClubStaff: (call?: { signal?: AbortSignal }) => http.get<ClubStaffMemberDto[]>('/club/staff', { signal: call?.signal }),
  registerClubStaff: (body: RegisterClubStaffBody) => http.post<ClubStaffMemberDto>('/club/staff', body),
  listHireableTrainerCards: (call?: { signal?: AbortSignal }) => http.get<TrainerCardWithoutLogin[]>('/club/staff/trainer-cards', { signal: call?.signal }),
  changeClubStaffRole: (userId: string, body: ChangeClubStaffRoleBody) =>
    http.patch<void>(`/club/staff/${userId}/role`, body),
  changeClubStaffContacts: (userId: string, body: ChangeClubStaffContactsBody) =>
    http.patch<ClubStaffContactsChanged>(`/club/staff/${userId}/contacts`, body),
  dismissClubStaff: (userId: string) => http.post<void>(`/club/staff/${userId}/dismiss`),

  getUserPermissions: (userId: string, call?: { signal?: AbortSignal }) =>
    http.get<GetUserPermissionsResponse>(`/admin/users/${userId}/permissions`, { signal: call?.signal }),
  grantUserPermission: (
    userId: string,
    payload: { permission: string; reason?: string | null; expiresAt?: string | null },
  ) => http.post(`/admin/users/${userId}/permissions/grant`, payload),
  revokeUserPermission: (
    userId: string,
    payload: { permission: string; reason?: string | null; expiresAt?: string | null },
  ) => http.post<void>(`/admin/users/${userId}/permissions/revoke`, payload),
  clearUserPermissionOverride: (userId: string, permission: string) =>
    http.delete<void>(`/admin/users/${userId}/permissions/${encodeURIComponent(permission)}`),

  listAudit: (limit = 50, call?: { signal?: AbortSignal }) =>
    http.get<{ items: SettingsAuditDto[] }>('/settings/audit', { query: { limit }, signal: call?.signal }),
  listAuditEvents: (filter: {
    entityType?: string;
    entityId?: string;
    userId?: string;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  },
  call?: { signal?: AbortSignal }) =>
    http.get<ListAuditEventsResponse>('/settings/audit/events', { query: filter, signal: call?.signal }),
  listAuditEntityTypes: (call?: { signal?: AbortSignal }) => http.get<AuditEntityTypeDto[]>('/settings/audit/entity-types', { signal: call?.signal }),
};

export const SETTINGS_IMPACT: Record<string, Array<{ label: string; route: string }>> = {
  'club.profile': [
    { label: 'Чек кассы', route: '/pos' },
    { label: 'Шапка писем', route: '/settings/communications' },
  ],
  'club.schedule': [
    { label: 'Шахматка', route: '/schedule' },
    { label: 'Бронирование', route: '/schedule' },
  ],
  grid: [{ label: 'Шахматка', route: '/schedule' }],
  'slot-policy': [
    { label: 'Создание брони', route: '/schedule' },
    { label: 'Отмена брони', route: '/bookings' },
    { label: 'Турниры — возвраты', route: '/tournaments' },
  ],
  'rating-policy': [
    { label: 'Матчи и рейтинг', route: '/matchmaking' },
    { label: 'Спорные результаты', route: '/matchmaking' },
  ],
  'client-category-rules': [{ label: 'Карточки клиентов', route: '/clients' }],
  'access-zones': [
    { label: 'Доступ и посещения', route: '/access' },
    { label: 'Категории карт', route: '/settings/pricing-loyalty/categories' },
  ],
  reminders: [{ label: 'Уведомления клиентам', route: '/settings/communications' }],
  dnd: [{ label: 'Уведомления', route: '/settings/communications' }],
  'owner-reports': [
    { label: 'Касса · Z-отчёт', route: '/pos' },
    { label: 'Финансы · P&L', route: '/finance' },
  ],
  'integrations.sigur': [{ label: 'Лента проходов', route: '/access' }],
  'integrations.sber_kkm': [{ label: 'Касса', route: '/pos' }],
  'integrations.sms': [
    { label: 'Уведомления клиентам', route: '/settings/communications' },
    { label: 'Вход клиентов по коду · моб.', route: '/clients' },
  ],
  'integrations.telegram_bot': [
    { label: 'Уведомления', route: '/settings/communications' },
  ],
  tax: [
    { label: 'Чек кассы', route: '/pos' },
    { label: 'Финансы', route: '/finance' },
  ],
  roles: [{ label: 'Сотрудники', route: '/staff' }],
};

export const impactFor = (path: string): Array<{ label: string; route: string }> =>
  SETTINGS_IMPACT[path] ?? [];

export interface SettingsNode {
  id: string;
  module:
    | 'club'
    | 'operations'
    | 'pricing-loyalty'
    | 'communications'
    | 'integrations'
    | 'access-security'
    | 'system'
    | 'fitness';
  title: string;
  subtitle: string;
  href: string;
  keywords: string[];
  comingSoon?: string;
  permission?: string;
}

export const kindToSegment = (kind: IntegrationKind): string =>
  kind.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

export const SETTINGS_NODES: SettingsNode[] = [
  {
    id: 'club-general',
    module: 'club',
    title: 'Общее',
    subtitle: 'Название, реквизиты, контакты',
    href: '/settings/club/general',
    keywords: ['название', 'ИНН', 'контакты', 'email', 'телефон'],
  },
  {
    id: 'club-hours',
    module: 'club',
    title: 'Расписание клуба',
    subtitle: 'Рабочие часы, выходные',
    href: '/settings/club/hours',
    keywords: ['часы', 'график', 'часовой пояс', 'выходные'],
  },
  {
    id: 'club-numbering',
    module: 'club',
    title: 'Нумерация карт',
    subtitle: 'memberPrefix, формат, счётчик',
    href: '/settings/club/numbering',
    keywords: ['номер', 'карта', 'FC', 'префикс'],
  },
  {
    id: 'club-app',
    module: 'club',
    title: 'Подключение приложения',
    subtitle: 'Номер клуба и QR для клиентов',
    href: '/settings/club/app',
    keywords: ['QR', 'приложение', 'номер клуба', 'привязка', 'код', 'мобильное'],
  },
  {
    id: 'club-branding',
    module: 'club',
    title: 'Брендинг',
    subtitle: 'Акцент, лого, шапка чека',
    href: '/settings/club/branding',
    keywords: ['логотип', 'цвет', 'бренд', 'чек'],
  },
  {
    id: 'club-split-pay',
    module: 'club',
    title: 'Сплит-оплата',
    subtitle: 'Поведение по умолчанию для долгов',
    href: '/settings/club/split-pay',
    keywords: ['сплит', 'split', 'долги', 'организатор'],
  },
  {
    id: 'ops-court-options',
    module: 'operations',
    permission: 'catalog:manage',
    title: 'Опции кортов',
    subtitle: 'Подогрев, свет, ракетки — надбавки',
    href: '/settings/operations/court-options',
    keywords: ['опции', 'плюшки', 'надбавка', 'подогрев', 'свет', 'ракетки'],
  },
  {
    id: 'ops-sports',
    module: 'operations',
    permission: 'catalog:manage',
    title: 'Виды спорта',
    subtitle: 'Названия, порядок, доступность',
    href: '/settings/operations/sports',
    keywords: ['спорт', 'падел', 'теннис', 'волейбол'],
  },
  {
    id: 'ops-courts',
    module: 'operations',
    permission: 'catalog:manage',
    title: 'Корты',
    subtitle: 'CRUD ресурсов и базовых цен',
    href: '/settings/operations/courts',
    keywords: ['корт', 'ресурс', 'цена'],
  },
  {
    id: 'ops-pos-catalog',
    module: 'operations',
    permission: 'catalog:manage',
    title: 'Касса · каталог',
    subtitle: 'Инвентарь и услуги в POS',
    href: '/settings/operations/pos-catalog',
    keywords: ['касса', 'pos', 'каталог', 'инвентарь', 'услуги', 'товары'],
  },
  {
    id: 'ops-rental-bundles',
    module: 'operations',
    permission: 'catalog:manage',
    title: 'Касса · пакеты проката',
    subtitle: 'Готовые наборы инвентаря одной кнопкой',
    href: '/settings/operations/rental-bundles',
    keywords: ['прокат', 'аренда', 'пакет', 'набор', 'ракетки', 'инвентарь', 'bundle'],
  },
  {
    id: 'ops-slot-policy',
    module: 'operations',
    title: 'Политика бронирования',
    subtitle: 'Hold, parallel, optimise, refund',
    href: '/settings/operations/slot-policy',
    keywords: ['слот', 'бронь', 'hold', 'возврат', 'no-show', 'отмена'],
  },
  {
    id: 'ops-rating-policy',
    module: 'operations',
    title: 'Рейтинг и результаты',
    subtitle: 'Вкл/выкл, окно подтверждения, затухание',
    href: '/settings/operations/rating-policy',
    keywords: ['рейтинг', 'уровень', 'матч', 'подтверждение', 'decay', 'openskill'],
  },
  {
    id: 'ops-tournament-norms',
    module: 'operations',
    permission: 'tournament:manage',
    title: 'Турниры · нормы времени',
    subtitle: 'Минуты на игру и пересменка — по ним считается окно турнира',
    href: '/settings/operations/tournament-norms',
    keywords: ['турнир', 'норма', 'минуты', 'игра', 'пересменка', 'окно', 'очки', 'геймы', 'сеты'],
  },
  {
    id: 'ops-grid',
    module: 'operations',
    title: 'Сетка расписания',
    subtitle: 'Шаг, плотность, акценты',
    href: '/settings/operations/grid',
    keywords: ['шахматка', 'сетка', 'плотность'],
  },
  {
    id: 'ops-trainers-screen',
    module: 'operations',
    title: 'Тренеры',
    subtitle: 'Состав, график и зарплаты — в разделе «Тренеры»',
    href: '/trainers',
    keywords: ['тренер', 'зарплата', 'ставка', 'процент'],
  },
  {
    id: 'pl-categories',
    module: 'pricing-loyalty',
    title: 'Категории клиентов',
    subtitle: 'Скидки, кэшбэк, авто-правила',
    href: '/settings/pricing-loyalty/categories',
    keywords: ['категория', 'скидка', 'кэшбэк', 'auto', 'VIP'],
  },
  {
    id: 'pl-bonus-program',
    module: 'pricing-loyalty',
    title: 'Бонусная программа',
    subtitle: 'Включение, подарок за регистрацию, срок, неактивность',
    href: '/settings/pricing-loyalty/bonus-program',
    keywords: ['бонусы', 'программа', 'подарок', 'регистрация', 'срок', 'сгорание', 'неактивность', 'предупреждение'],
  },
  {
    id: 'pl-bonus-payment',
    module: 'pricing-loyalty',
    title: 'Бонусы и оплата',
    subtitle: 'Правила начисления и трат бонусов, активная карта',
    href: '/settings/pricing-loyalty/bonus-payment',
    keywords: ['бонусы', 'начисление', 'карта', 'активная', 'кэшбэк', 'лимит'],
  },
  {
    id: 'pl-refunds-discounts',
    module: 'pricing-loyalty',
    title: 'Возвраты и скидки',
    subtitle: 'Порог утверждения возврата, потолок скидки кассира, автовозврат',
    href: '/settings/pricing-loyalty/refunds-discounts',
    keywords: ['возврат', 'утверждение', 'порог', 'скидка', 'касса', 'потолок', 'автовозврат'],
  },
  {
    id: 'fitness-general',
    module: 'fitness',
    title: 'Раздел в приложении',
    subtitle: 'Включение раздела у клиента, «в зале сейчас»',
    href: '/settings/fitness/general',
    keywords: ['фитнес', 'раздел', 'выключить', 'включить', 'приложение', 'в зале сейчас', 'загруженность'],
  },
  {
    id: 'fitness-composition',
    module: 'fitness',
    title: 'Состав',
    subtitle: 'Зал, групповые, персональные, бассейн, сауна',
    href: '/settings/fitness/composition',
    keywords: ['фитнес', 'зал', 'занятия', 'персональные', 'бассейн', 'сауна'],
  },
  {
    id: 'fitness-visits',
    module: 'fitness',
    title: 'Учёт посещений',
    subtitle: 'Что считать посещением и что списывает абонемент',
    href: '/settings/fitness/visits',
    keywords: ['фитнес', 'посещения', 'проход', 'турникет', 'списание'],
  },
  {
    id: 'fitness-tariffs',
    module: 'fitness',
    title: 'Тарифы',
    subtitle: 'Абонементы фитнеса: зал, занятия, персональные',
    href: '/settings/fitness/tariffs',
    keywords: ['фитнес', 'тариф', 'абонемент', 'шаблон', 'цена'],
  },
  {
    id: 'pl-subscriptions',
    module: 'pricing-loyalty',
    title: 'Шаблоны абонементов',
    subtitle: 'Тарифы, цены, период, заморозка по умолчанию',
    href: '/settings/pricing-loyalty/subscriptions',
    keywords: ['абонемент', 'подписка', 'тариф', 'заморозка', 'пауза'],
  },
  {
    id: 'pl-promocodes',
    module: 'pricing-loyalty',
    title: 'Промокоды',
    subtitle: 'WELCOME10, бюджет, период',
    href: '/settings/pricing-loyalty/promocodes',
    keywords: ['промокод', 'скидка'],
  },
  {
    id: 'pl-tax',
    module: 'pricing-loyalty',
    title: 'Налоги и комиссии',
    subtitle: 'НДС, эквайринг, СБП',
    href: '/settings/pricing-loyalty/tax',
    keywords: ['ндс', 'налог', 'комиссия', 'эквайринг'],
  },
  {
    id: 'cm-news',
    module: 'communications',
    title: 'Новости',
    subtitle: 'Анонсы клуба на главном экране приложения',
    href: '/news',
    keywords: ['новости', 'анонс', 'контент', 'публикация'],
  },
  {
    id: 'cm-quick-replies',
    module: 'communications',
    title: 'Быстрые ответы',
    subtitle: 'Заготовки для чата поддержки',
    href: '/settings/communications/quick-replies',
    keywords: ['чат', 'быстрые ответы', 'поддержка', 'шаблоны чата', 'канед'],
  },
  {
    id: 'cm-reminders',
    module: 'communications',
    title: 'Напоминания',
    subtitle: '24ч / 2ч / 30мин до брони',
    href: '/settings/communications/reminders',
    keywords: ['напоминание', 'reminder', 'push'],
  },
  {
    id: 'cm-dnd',
    module: 'communications',
    title: 'Не беспокоить',
    subtitle: 'Ночные часы, исключения',
    href: '/settings/communications/dnd',
    permission: 'notification:manage',
    keywords: ['тишина', 'ночь', 'DND'],
  },
  {
    id: 'cm-owner-reports',
    module: 'communications',
    title: 'Отчёты владельцу',
    subtitle: 'Z-отчёт смены и итоги дня письмом',
    href: '/settings/communications/owner-reports',
    permission: 'notification:manage',
    keywords: ['отчёт', 'Z-отчёт', 'итоги дня', 'сводка', 'почта', 'письмо', 'владелец', 'управляющий'],
  },
  {
    id: 'cm-notification-rules',
    module: 'communications',
    title: 'Правила уведомлений',
    subtitle: 'Какие категории клиент может отключить',
    href: '/settings/communications/notification-rules',
    permission: 'notification:manage',
    keywords: ['уведомления', 'категории', 'отключить', 'push', 'опт-аут', 'политика'],
  },
  {
    id: 'cm-event-templates',
    module: 'communications',
    title: 'Шаблоны событий',
    subtitle: 'Тексты системных уведомлений пайплайна',
    href: '/settings/communications/event-templates',
    permission: 'notification:manage',
    keywords: ['уведомления', 'шаблоны событий', 'push', 'email', 'тексты', 'переменные'],
  },
  {
    id: 'cm-broadcast',
    module: 'communications',
    title: 'Рассылка',
    subtitle: 'Push всем клиентам клуба',
    href: '/settings/communications/broadcast',
    permission: 'notification:broadcast',
    keywords: ['рассылка', 'broadcast', 'push', 'маркетинг', 'анонс', 'массовая'],
  },
  {
    id: 'comm-friends',
    module: 'communications',
    title: 'Друзья',
    subtitle: 'Тумблер, лимиты заявок и ссылок',
    href: '/settings/communications/friends',
    keywords: ['друзья', 'заявки', 'инвайт', 'QR', 'лимит'],
  },
  {
    id: 'in-sigur',
    module: 'integrations',
    title: 'СКУД Sigur',
    subtitle: 'API key, частота синхронизации',
    href: '/settings/integrations/sigur',
    keywords: ['сигур', 'sigur', 'скуд'],
  },
  {
    id: 'in-sber',
    module: 'integrations',
    title: 'Касса и фискальный чек',
    subtitle: 'ККМ-служба на компьютере кассира: адрес, учётная запись, ИНН кассы',
    href: '/settings/integrations/sber-kkm',
    keywords: ['атол', 'ккм', 'pos', 'фискалка', 'касса', 'чек', '54-фз', 'kkmserver', 'эквайринг'],
  },
  {
    id: 'in-payment-providers',
    module: 'integrations',
    title: 'Приём платежей',
    subtitle: 'CloudPayments, Сбер, Т-Банк — ключи клуба',
    href: '/settings/payment-providers',
    keywords: ['эквайринг', 'платежи', 'банк', 'сбп', 'cloudpayments', 'тбанк', 'сбер'],
  },
  {
    id: 'in-sms',
    module: 'integrations',
    title: 'SMS-шлюз',
    subtitle: 'Провайдер, ключи клуба, имя отправителя',
    href: '/settings/integrations/sms',
    keywords: ['sms', 'смс', 'смсц', 'smsc', 'smsint', 'шлюз', 'отправитель', 'подпись',
      'токен'],
  },
  {
    id: 'in-telegram',
    module: 'integrations',
    title: 'Telegram-бот',
    subtitle: 'Токен бота',
    href: '/settings/integrations/telegram-bot',
    keywords: ['telegram', 'бот', 'tg'],
  },
  {
    id: 'in-max',
    module: 'integrations',
    title: 'Max (VK)',
    subtitle: 'Авторизация клиентов',
    href: '/settings/integrations/max-app',
    keywords: ['max', 'vk'],
    comingSoon: 'Авторизация через Max ещё не подключена',
  },
  {
    id: 'as-roles',
    module: 'access-security',
    title: 'Роли и права',
    subtitle: 'Что может каждая роль: встроенное, добавленное и снятое клубом',
    href: '/settings/access-security/roles',
    keywords: ['роль', 'доступ', 'права', 'галочка'],
  },
  {
    id: 'as-zones',
    module: 'access-security',
    permission: 'access:manage',
    title: 'Зоны доступа',
    subtitle: 'Карты × Зоны (Sigur)',
    href: '/settings/access-security/zones',
    keywords: ['зона', 'этаж', 'сауна', 'скуд'],
  },
  {
    id: 'as-devices',
    module: 'access-security',
    permission: 'access:manage',
    title: 'Устройства',
    subtitle: 'Турникеты, замки и ворота',
    href: '/settings/access-security/devices',
    keywords: ['устройство', 'турникет', 'замок', 'ворота', 'sigur', 'скуд'],
  },
  {
    id: 'as-audit',
    module: 'access-security',
    title: 'Аудит-лог',
    subtitle: '50 последних изменений настроек',
    href: '/settings/access-security/audit',
    keywords: ['аудит', 'история', 'log'],
  },
  {
    id: 'as-actions',
    module: 'access-security',
    title: 'Журнал действий',
    subtitle: 'Кто и что поменял в клубе — платежи, чеки, клиенты, брони',
    href: '/settings/access-security/actions',
    keywords: ['журнал', 'действия', 'кто изменил', 'аудит', 'история', 'платёж'],
  },
  {
    id: 'sys-backups',
    module: 'system',
    title: 'Резервные копии',
    subtitle: 'Расписание, скачивание',
    href: '/settings/system/backups',
    keywords: ['бэкап', 'backup'],
  },
  {
    id: 'sys-import',
    module: 'system',
    title: 'Импорт / экспорт',
    subtitle: 'CSV клиентов, миграция из fitbase',
    href: '/settings/system/import-export',
    keywords: ['импорт', 'экспорт', 'csv', 'fitbase'],
  },
  {
    id: 'sys-about',
    module: 'system',
    title: 'О системе',
    subtitle: 'Версия, окружение, миграции',
    href: '/settings/system/about',
    keywords: ['версия', 'about'],
  },
];

export interface WorkingHoursEntry {
  dayOfWeek: number;
  startMin: number;
  endMin: number;
  closed: boolean;
}

export interface ClubScheduleConfig {
  weeklyHours: WorkingHoursEntry[];
  timezone: string;
}

export interface ClubSchedulePatch {
  weeklyHours?: ClubScheduleConfig['weeklyHours'];
}

const HHMMSS_RX = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

const WEEKDAYS: readonly DayOfWeek[] = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

const timeToMin = (s: string): number => {
  const m = HHMMSS_RX.exec(s);
  if (!m) return 0;
  return (Number(m[1]) | 0) * 60 + (Number(m[2]) | 0);
};

const minToTime = (mins: number): string => {
  const safe = Number.isFinite(mins) ? Math.max(0, Math.min(1439, mins | 0)) : 0;
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
};

export function adaptScheduleFromBe(dto: ClubScheduleDto): ClubScheduleConfig {
  return {
    weeklyHours: dto.weeklyHours.map((d) => ({
      dayOfWeek: Math.max(0, WEEKDAYS.indexOf(d.dayOfWeek)),
      startMin: timeToMin(d.openAt),
      endMin: timeToMin(d.closeAt),
      closed: !d.isOpen,
    })),
    timezone: typeof dto.timezone === 'string' ? dto.timezone : '',
  };
}

export function adaptSchedulePatchToBe(patch: ClubSchedulePatch): ClubSchedulePayload {
  if (!patch.weeklyHours) return {};
  return {
    weeklyHours: patch.weeklyHours.map((d) => ({
      dayOfWeek: WEEKDAYS[d.dayOfWeek] ?? DayOfWeek.SUNDAY,
      isOpen: !d.closed,
      openAt: minToTime(d.startMin),
      closeAt: minToTime(d.endMin),
    })),
  };
}
